import * as devConfig from "../../../app/configs/devConfig";
import { getAuthHeader } from "../../../app/globalState/GlobalAuthState";
import { AiAskRequest, AiAskResponse } from "../../../slices/AiSlice";

/*
 * "Ask ProCharted" with progress (2026-10-22, faster Ask): POST api/ai/ask/stream
 * answers with Server-Sent Events: `step` {text, round} while the server looks
 * things up ("Looking up overdue work…"), then `answer` (exactly the JSON of
 * api/ai/ask) or `error` {status, message}. Plain fetch + ReadableStream (not
 * EventSource: it can't POST or send the Authorization header); the two-step
 * header (X-Proc-2fa) is added by installTwoStepHeader, which wraps fetch.
 *
 * Word by word (owner 2026-10-22): `text` {delta, reset, round} events carry
 * the answer while the model writes it (reset = drop what was streamed so
 * far). The final `answer` event replaces the streamed text.
 */

/** A piece of the answer while it is written (`text` event). */
export type AskTextUpdate = { delta: string; reset: boolean };

/** The streamed answer text after one `text` event. */
export const applyStreamText = (
  current: string,
  update: AskTextUpdate,
): string => (update.reset ? "" : current) + update.delta;

/** Reads a `text` event's data; null when it isn't one. */
export const readTextUpdate = (parsed: unknown): AskTextUpdate | null => {
  if (!parsed || typeof parsed !== "object") return null;
  const data = parsed as Record<string, unknown>;
  const delta = typeof data.delta === "string" ? data.delta : "";
  const reset = data.reset === true;
  return delta || reset ? { delta, reset } : null;
};

/**
 * What the answer bubble shows while the text streams in: the plain text
 * without half-written **bold** markers (the final answer is shown with
 * HelpReplyText's formatting).
 */
export const streamingDisplayText = (text: string): string =>
  text.replace(/\*\*/g, "").replace(/^\s+/, "");

export type SseEvent = { event: string; data: string };

/**
 * Splits received text into complete events (separated by a blank line);
 * the unfinished end is returned as `rest` for the next chunk.
 */
export const parseSse = (
  buffer: string,
): { events: SseEvent[]; rest: string } => {
  const text = buffer.replace(/\r\n/g, "\n");
  const parts = text.split("\n\n");
  const rest = parts.pop() ?? "";
  const events = parts
    .map((chunk) => {
      let event = "message";
      const data: string[] = [];
      chunk.split("\n").forEach((line) => {
        if (line.startsWith(":")) return; // comment / keep-alive
        const colon = line.indexOf(":");
        const field = colon < 0 ? line : line.slice(0, colon);
        let value = colon < 0 ? "" : line.slice(colon + 1);
        if (value.startsWith(" ")) value = value.slice(1);
        if (field === "event") event = value;
        else if (field === "data") data.push(value);
      });
      return { event, data: data.join("\n") };
    })
    .filter((e) => e.data !== "");
  return { events, rest };
};

/** The stream can't be used (older server, proxy or network problem): ask with api/ai/ask instead. */
export class AskStreamUnavailable extends Error {
  constructor(reason: string) {
    super(`Ask stream unavailable: ${reason}`);
    this.name = "AskStreamUnavailable";
  }
}

/**
 * A problem the server explained (not set up, daily limit, AI busy...). `error`
 * has RTK Query's shape ({status, data}) so `aiErrorMessage` reads it as before.
 */
export class AskStreamFailed extends Error {
  readonly error: { status: number; data: unknown };

  constructor(error: { status: number; data: unknown }) {
    super("Ask failed");
    this.name = "AskStreamFailed";
    this.error = error;
  }
}

/** Server statuses that mean "no stream here", not "your question failed". */
export const streamUnavailableStatus = (status: number): boolean =>
  status === 404 || status === 405 || status === 415 || status >= 500;

const readJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

export const askStreamUrl = (): string =>
  `${devConfig.getServerBaseUrlForEnv().replace(/\/+$/, "")}/api/ai/ask/stream`;

/**
 * Asks through the stream; `onStep` gets each progress line and `onText` each
 * piece of the answer as it is written. Rejects with AbortError when stopped.
 */
export const streamAsk = async (
  body: AiAskRequest,
  {
    signal,
    onStep,
    onText,
  }: {
    signal: AbortSignal;
    onStep: (text: string) => void;
    onText?: (update: AskTextUpdate) => void;
  },
): Promise<AiAskResponse> => {
  let response: Response;
  try {
    response = await fetch(askStreamUrl(), {
      method: "POST",
      headers: {
        Authorization: getAuthHeader().Authorization,
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (signal.aborted) throw error;
    throw new AskStreamUnavailable("network");
  }

  const type = response.headers.get("content-type") || "";
  if (!response.ok || !type.includes("text/event-stream")) {
    if (response.ok || streamUnavailableStatus(response.status)) {
      throw new AskStreamUnavailable(`status ${response.status}`);
    }
    // 400 / 403 / 429 before anything was sent: same message as api/ai/ask.
    throw new AskStreamFailed({
      status: response.status,
      data: readJson(await response.text()),
    });
  }
  if (!response.body) throw new AskStreamUnavailable("no body");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { value, done } = await reader.read();
      buffer += done
        ? decoder.decode()
        : decoder.decode(value, { stream: true });
      const { events, rest } = parseSse(done ? `${buffer}\n\n` : buffer);
      buffer = rest;
      for (const { event, data } of events) {
        const parsed = readJson(data) as Record<string, unknown> | string;
        if (event === "step") {
          const text =
            typeof parsed === "object" &&
            parsed &&
            typeof parsed.text === "string"
              ? parsed.text.trim()
              : "";
          if (text) onStep(text);
        } else if (event === "text") {
          const update = readTextUpdate(parsed);
          if (update) onText?.(update);
        } else if (event === "answer" && typeof parsed === "object" && parsed) {
          return parsed as unknown as AiAskResponse;
        } else if (event === "error") {
          const status =
            typeof parsed === "object" &&
            parsed &&
            typeof parsed.status === "number"
              ? parsed.status
              : 400;
          throw new AskStreamFailed({ status, data: parsed });
        }
      }
      if (done) break;
    }
  } catch (error) {
    if (error instanceof AskStreamFailed || signal.aborted) throw error;
    throw new AskStreamUnavailable("stream broke");
  } finally {
    reader.releaseLock?.();
  }
  throw new AskStreamUnavailable("ended without an answer");
};

/** Progress lines shown in the "Thinking…" box: newest last, no repeats, at most `max`. */
export const addStep = (steps: string[], text: string, max = 4): string[] => {
  const line = text.trim();
  if (!line || steps[steps.length - 1] === line) return steps;
  return [...steps.filter((s) => s !== line), line].slice(-max);
};
